import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { PaginationRequest } from "src/app/models/common/paginationRequest";
import { GridUtils } from "../../utils/gridUtils";
import { AppSessionService } from "../app-session/app-session.service";
import { FilterOperation } from "src/app/models/custom-table/custom-table.model";
import {
  setSimpleFilterQuery,
  setAdvancedFilterQuery,
} from "../../utils/tableUtils";

const EMPTY_STRING = "";

@Injectable({
  providedIn: "root",
})
export class CardsService {
  public remoteData: BehaviorSubject<any[]>;

  constructor(private http: HttpClient, private appSession: AppSessionService) {
    this.remoteData = new BehaviorSubject([]);
  }

  getCardPlafond(cardCode: string): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    const url = `${this.appSession.baseUrl}/api/v1/cards/${cardCode}/plafond`;

    return this.http.get(url, { headers });
  }

  getCardSystems(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/cards"}/systems`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 40,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getCardLayouts(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/cards"}/layouts`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 40,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getCardStatus(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/cards"}/status`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 40,
        realSize: false,
      };
    }
    return this.http.get(url, { headers });
  }

  createCard(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.card);
    const url = `${this.appSession.baseUrl + "/api/v1/cards"}`;
    return this.http.post(url, data, { headers });
  }

  updateCard(card): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const data = JSON.stringify(card);
    const url = `${this.appSession.baseUrl + "/api/v1/cards"}/${card.code}`;
    return this.http.put(url, data, { headers });
  }

  updateCardPrompts(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const data = JSON.stringify(action.promptCode);
    const url = `${this.appSession.baseUrl + "/api/v1/cards"}/${
      action.cardCode
    }/prompts`;
    return this.http.put(url, data, { headers });
  }

  deleteCardPrompts(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    let data;
    data = JSON.stringify(action.promptCode);
    const url =
      `${this.appSession.baseUrl + "/api/v1/cards"}/` +
      encodeURIComponent(action.cardCode) +
      `/prompts`;
    return this.http.request("delete", url, { headers, body: data });
  }

  getFiltredCardList(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any,
    container?: Map<string, string>
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }
    return this.http.get(
      this.buildDataUrl(
        paging,
        filteringArgs,
        sortingArgs,
        headers,
        "/search?"
      ),
      { headers }
    );
  }

  getCard(code): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.appSession.baseUrl + "/api/v1/cards"}/${code}`;
    return this.http.get(url, { headers });
  }

  // save card limits
  saveCardLimits(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.cardLimitCodes);
    const url =
      `${this.appSession.baseUrl + "/api/v1/cards"}/` +
      encodeURIComponent(action.cardCode) +
      `/limits`;
    return this.http.post(url, data, { headers });
  }

  // reset card limit
  resetCardLimit(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.limitCode);
    const url =
      `${this.appSession.baseUrl + "/api/v1/cards"}/` +
      encodeURIComponent(action.cardCode) +
      `/limits/` +
      encodeURIComponent(action.limitCode) +
      `/resetLimit`;
    return this.http.post(url, data, { headers });
  }

  // reset card pin
  resetCardPin(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url =
      `${this.appSession.baseUrl + "/api/v1/cards"}/` +
      encodeURIComponent(action.cardCode) +
      `/resetPin`;
    return this.http.post(url, "", { headers });
  }

  // delete card Limits
  deleteCardLimits(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let data = "";

    if (action.cardLimitCodes.length > 0) {
      action.cardLimitCodes.forEach((cardLimitCode) => {
        if (data.length === 0) {
          data = data.concat("?limitCode=" + cardLimitCode);
        } else {
          data = data.concat("&limitCode=" + cardLimitCode);
        }
      });
    }

    const url =
      `${this.appSession.baseUrl + "/api/v1/cards"}/` +
      encodeURIComponent(action.cardCode) +
      `/limits` +
      data;
    return this.http.request("delete", url, { headers });
  }

  public getCardTransactions(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any
  ): Observable<any> {
    let headers = new HttpHeaders();

    let filterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    const url = `${
      this.appSession.baseUrl + "/api/v1/contractMovements/search"
    }`;
    const cardCode = sessionStorage.getItem("cardCode");
    const sortedLoadingAction = sessionStorage.getItem("sortposDateDESC");

    if (sortingArgs && sortingArgs.length > 0) {
      sortingArgs.forEach((columnSorting, idx) => {
        if (idx < sortingArgs.length - 1) {
          orderQuery += GridUtils._buildSortExpression(columnSorting) + ";";
        } else {
          orderQuery += GridUtils._buildSortExpression(columnSorting);
        }
      });
    } else {
      orderQuery = sortedLoadingAction;
    }

    if (filterQuery?.length > 0) {
      filterQuery += `movementTypeCode=like='DEBIT' and debitMovement.card.code == ${cardCode}`;
    } else {
      filterQuery = `movementTypeCode=like='DEBIT' and debitMovement.card.code == ${cardCode}`;
    }

    if (filterQuery.length > 0) {
      headers = headers.append("query", filterQuery);
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    return this.http.get(url, { headers });
  }

  private buildDataUrl(
    pagingArgs: any,
    filteringArgs: any,
    sortingArgs: any,
    header: HttpHeaders,
    path: string
  ): string {
    let baseQueryString = `${this.appSession.baseUrl + "/api/v1/cards"}${path}`;
    let scrollingQuery = EMPTY_STRING;
    let query = EMPTY_STRING;

    if (pagingArgs) {
      scrollingQuery = GridUtils._buildScrollExpression(pagingArgs);
    }

    query +=
      scrollingQuery !== EMPTY_STRING ? `&${scrollingQuery}` : EMPTY_STRING;

    baseQueryString += query;

    return baseQueryString;
  }
}
